/**
 * Phase J.3.2 — Preferences page. Same component used for customer and
 * admin via the `role` prop. Renders a table of event types with bell /
 * email toggles + a Reset-to-defaults action.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { usePreferences } from "@/hooks/useNotifications";
import {
    DEFAULT_PREFS, getEventMeta, deliverableForRole,
} from "@/lib/notificationEvents";


function StatusPill({ flags }) {
    if (!flags) return null;
    const bell = !!flags.bell;
    const email = !!flags.email;
    let label, cls;
    if (bell && email) {
        label = "Active";
        cls = "bg-emerald-50 text-emerald-900 ring-emerald-200";
    } else if (bell && !email) {
        label = "Bell only";
        cls = "bg-blue-50 text-blue-900 ring-blue-200";
    } else if (!bell && email) {
        label = "Email only";
        cls = "bg-amber-50 text-amber-900 ring-amber-200";
    } else {
        label = "Muted";
        cls = "bg-slate-100 text-slate-700 ring-slate-200";
    }
    return (
        <span className={"inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset " + cls}>
            {label}
        </span>
    );
}


export function NotificationPreferencesPage({ role }) {
    const { prefs, catalog, loading, error, savingKey, patchOne, refresh } = usePreferences({ role });
    const visible = deliverableForRole(role).filter((k) => catalog.includes(k));

    const handleToggle = (eventType, key, next) => {
        patchOne(eventType, { [key]: next });
    };

    const handleResetDefaults = async () => {
        // PATCH the entire default map.
        await Promise.all(
            visible.map((k) => patchOne(k, { ...DEFAULT_PREFS[k] })),
        );
        toast.success("Notification preferences restored to defaults.");
        await refresh();
    };

    const backHref = role === "customer" ? "/customer/notifications" : "/admin/notifications";

    return (
        <DashboardShell>
            <div data-testid="notification-prefs-page" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-0 py-6 space-y-5">
                <header>
                    <Link to={backHref} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={14} aria-hidden="true" />
                        Back to notifications
                    </Link>
                    <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Notifications</p>
                            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Preferences</h1>
                            <p className="text-sm text-slate-500 mt-1">
                                Pick what you want in your bell and inbox. Changes save instantly.
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            data-testid="prefs-reset-defaults"
                            onClick={handleResetDefaults}
                            disabled={loading}
                            className="gap-1.5"
                        >
                            <RefreshCcw size={14} aria-hidden="true" />
                            Reset to defaults
                        </Button>
                    </div>
                </header>

                {error && (
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                        {error}
                    </div>
                )}

                <section className="bg-white rounded-lg ring-1 ring-slate-200 overflow-hidden">
                    <div className="hidden md:grid grid-cols-[1.6fr_88px_88px_92px] gap-4 px-4 py-2 border-b border-slate-100 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        <div>Event</div>
                        <div className="text-center">Bell</div>
                        <div className="text-center">Email</div>
                        <div className="text-center">Status</div>
                    </div>

                    {loading && visible.length === 0 ? (
                        <div className="px-4 py-16 text-center text-sm text-slate-500">Loading…</div>
                    ) : (
                        visible.map((eventType) => {
                            const meta = getEventMeta(eventType);
                            const Icon = meta.icon;
                            const flags = (prefs || {})[eventType] || DEFAULT_PREFS[eventType];
                            const saving = savingKey === eventType;
                            return (
                                <div
                                    key={eventType}
                                    data-testid={`prefs-row-${eventType}`}
                                    className="grid grid-cols-1 md:grid-cols-[1.6fr_88px_88px_92px] gap-4 items-center px-4 py-3.5 border-b border-slate-100 last:border-0"
                                >
                                    <div className="flex items-start gap-3 min-w-0">
                                        <span className={"mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full ring-1 ring-inset " + meta.accent}>
                                            <Icon size={14} aria-hidden="true" />
                                        </span>
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-slate-900">{meta.label}</p>
                                            <p className="text-xs text-slate-500 mt-0.5">{meta.description}</p>
                                        </div>
                                    </div>
                                    <div className="md:flex md:justify-center">
                                        <label className="inline-flex md:flex-col items-center gap-2">
                                            <span className="text-[11px] text-slate-500 md:hidden">Bell</span>
                                            <Switch
                                                checked={!!flags.bell}
                                                onCheckedChange={(v) => handleToggle(eventType, "bell", v)}
                                                disabled={saving}
                                                data-testid={`prefs-toggle-${eventType}-bell`}
                                            />
                                        </label>
                                    </div>
                                    <div className="md:flex md:justify-center">
                                        <label className="inline-flex md:flex-col items-center gap-2">
                                            <span className="text-[11px] text-slate-500 md:hidden">Email</span>
                                            <Switch
                                                checked={!!flags.email}
                                                onCheckedChange={(v) => handleToggle(eventType, "email", v)}
                                                disabled={saving}
                                                data-testid={`prefs-toggle-${eventType}-email`}
                                            />
                                        </label>
                                    </div>
                                    <div className="md:flex md:justify-center">
                                        {saving ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                                                <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                                                Saving…
                                            </span>
                                        ) : (
                                            <StatusPill flags={flags} />
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </section>

                <p className="text-[11px] text-slate-400 text-center pt-2">
                    Muted events are still recorded in your inbox archive — you can switch them back on anytime.
                </p>
            </div>
        </DashboardShell>
    );
}

export default NotificationPreferencesPage;
